// frontend/src/admin/pages/AdminDashboard.js - DASHBOARD MODERNE
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  DollarSign,
  Brain,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  RefreshCw,
  ArrowUpRight
} from 'lucide-react';

import api from '../../services/api';
import styles from '../styles/AdminDashboard.module.css';

const AdminDashboard = ({ adminUser, liveStats, onPageChange }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Charger les données dashboard
  const fetchDashboardData = async () => {
    try {
      setError(null);
      const response = await api.get('/admin/dashboard');
      const data = response.data;

      if (data && data.success) {
        setDashboardData(data);
        setLastUpdate(new Date());
        console.log('✅ Données dashboard chargées:', data);
      } else {
        console.error('❌ Erreur API dashboard:', data?.message);
        setError(data?.message || 'Erreur API');
      }
    } catch (err) {
      console.error('❌ Erreur réseau dashboard:', err);
      setError(err.message || 'Erreur réseau');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    onPageChange?.('dashboard');
    fetchDashboardData();

    const interval = setInterval(fetchDashboardData, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [onPageChange]);

  // Rafraîchir manuellement
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
  };

  // Action rapide : créer une alerte test
  const createTestAlert = async () => {
    try {
      await api.post('/admin/monitoring/test-alert', {
        severity: 'low'
      });
      alert('✅ Alerte de test créée avec succès !');
    } catch (err) {
      console.error('❌ Erreur création alerte test:', err);
      alert('❌ Erreur lors de la création de l\'alerte test');
    }
  };

  if (loading) {
    return (
      <div className={styles.adminDashboardLoading}>
        <div className={styles.adminLoadingSpinner}></div>
        <h2>🔄 Chargement du dashboard…</h2>
        <p>Récupération des métriques en cours</p>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className={styles.adminDashboardError}>
        <AlertTriangle size={48} />
        <h2>❌ Erreur de chargement du dashboard</h2>
        <p>{error || 'Impossible de récupérer les données du dashboard.'}</p>
        <button 
          className={styles.adminBtn}
          onClick={() => window.location.reload()}
        >
          🔄 Recharger la page
        </button>
      </div>
    );
  }

  const { stats = {}, alerts = [], admin_info = {} } = dashboardData;

  // Calculer les tendances
  const getTrendIcon = (value) => {
    if (!value) return null;
    return value >= 0 ? (
      <TrendingUp size={16} className={styles.trendPositive} />
    ) : (
      <TrendingDown size={16} className={styles.trendNegative} />
    );
  };

  return (
    <div className={styles.adminDashboard}>
      {/* Header Dashboard */}
      <div className={styles.adminDashboardHeader}>
        <div className={styles.adminDashboardTitle}>
          <h1>🎯 Dashboard Administration</h1>
          <p>
            Bonjour <strong>{admin_info.admin_name || adminUser?.username || 'Admin'}</strong>, 
            voici la vue globale de la plateforme DragueMaster.
          </p>
        </div>

        <div className={styles.adminDashboardActions}>
          <div className={styles.adminDashboardMeta}>
            <div className={styles.adminMetaItem}>
              <Clock size={16} />
              <span>Dernière MAJ</span>
              <strong>{lastUpdate ? lastUpdate.toLocaleTimeString('fr-FR') : '—'}</strong>
            </div>
            <div className={styles.adminMetaItem}>
              <Users size={16} />
              <span>Rôle</span>
              <strong>{adminUser?.role || 'super_admin'}</strong>
            </div>
          </div>

          <button
            className={`${styles.adminBtn} ${styles.adminBtnSecondary}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? styles.spinning : ''} />
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </button>

          <button
            className={`${styles.adminBtn} ${styles.adminBtnPrimary}`}
            onClick={createTestAlert}
          >
            <Zap size={18} />
            Test Alerte
          </button>
        </div>
      </div>

      {/* Métriques principales */}
      <div className={styles.adminMetricsGrid}>
        {/* Utilisateurs */}
        <div className={`${styles.adminMetricCard} ${styles.adminMetricCardUsers}`}>
          <div className={styles.adminMetricCardHeader}>
            <div className={styles.adminMetricIcon}>
              <Users size={24} />
            </div>
            <Link to="/admin/users" className={styles.adminMetricLink}>
              Voir tous
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className={styles.adminMetricContent}>
            <h3>Utilisateurs</h3>
            <div className={styles.adminMetricValue}>
              {stats.total_users?.toLocaleString('fr-FR') ?? '—'}
            </div>
            <div className={styles.adminMetricSubtitle}>
              <span className={styles.adminMetricBadge}>
                +{stats.new_users_today ?? 0}
              </span>
              nouveaux aujourd'hui
            </div>
          </div>

          <div className={styles.adminMetricFooter}>
            <Activity size={14} />
            <span>{stats.active_users_24h ?? 0} actifs (24h)</span>
          </div>
        </div>

        {/* Conversations */}
        <div className={`${styles.adminMetricCard} ${styles.adminMetricCardConversations}`}>
          <div className={styles.adminMetricCardHeader}>
            <div className={styles.adminMetricIcon}>
              <MessageSquare size={24} />
            </div>
          </div>

          <div className={styles.adminMetricContent}>
            <h3>Conversations</h3>
            <div className={styles.adminMetricValue}>
              {stats.total_conversations?.toLocaleString('fr-FR') ?? '—'}
            </div>
            <div className={styles.adminMetricSubtitle}>
              <span className={styles.adminMetricBadge}>
                +{stats.conversations_today ?? 0}
              </span>
              créées aujourd'hui
            </div>
          </div>

          <div className={styles.adminMetricFooter}>
            <MessageSquare size={14} />
            <span>{stats.messages_today ?? 0} messages aujourd'hui</span>
          </div>
        </div>

        {/* Revenus */}
        <div className={`${styles.adminMetricCard} ${styles.adminMetricCardRevenue}`}>
          <div className={styles.adminMetricCardHeader}>
            <div className={styles.adminMetricIcon}>
              <DollarSign size={24} />
            </div>
            <Link to="/admin/payments" className={styles.adminMetricLink}>
              Détails
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className={styles.adminMetricContent}>
            <h3>Revenus du mois</h3>
            <div className={styles.adminMetricValue}>
              {stats.revenue_month
                ? `${stats.revenue_month.toFixed(2)}€`
                : '—'}
            </div>
            <div className={styles.adminMetricSubtitle}>
              <span className={styles.adminMetricBadge}>
                {stats.active_subscriptions ?? 0}
              </span>
              abonnements actifs
            </div>
          </div>

          <div className={styles.adminMetricFooter}>
            {getTrendIcon(stats.revenue_trend)}
            <span>
              {stats.revenue_trend 
                ? `${Math.abs(stats.revenue_trend).toFixed(1)}% vs mois dernier`
                : 'Pas de données'
              }
            </span>
          </div>
        </div>

        {/* IA Usage */}
        <div className={`${styles.adminMetricCard} ${styles.adminMetricCardAi}`}>
          <div className={styles.adminMetricCardHeader}>
            <div className={styles.adminMetricIcon}>
              <Brain size={24} />
            </div>
            <Link to="/admin/ai-usage" className={styles.adminMetricLink}>
              Voir plus
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className={styles.adminMetricContent}>
            <h3>Utilisation IA</h3>
            <div className={styles.adminMetricValue}>
              {stats.tokens_used_today?.toLocaleString('fr-FR') ?? '—'}
            </div>
            <div className={styles.adminMetricSubtitle}>
              <span className={styles.adminMetricBadge}>
                {stats.ai_requests_today ?? 0}
              </span>
              requêtes aujourd'hui
            </div>
          </div>

          <div className={styles.adminMetricFooter}>
            <Zap size={14} />
            <span>Tokens utilisés aujourd'hui</span>
          </div>
        </div>
      </div>

      {/* Stats Live */}
      {liveStats && (
        <div className={styles.adminLiveStats}>
          <div className={styles.adminLiveStatsHeader}>
            <h2>
              <Activity size={20} />
              Statistiques en Temps Réel
            </h2>
            <div className={styles.liveIndicator}>
              <span className={styles.liveDot}></span>
              Live
            </div>
          </div>

          <div className={styles.adminLiveStatsGrid}>
            <div className={styles.adminLiveStat}>
              <div className={styles.liveStatIcon}>
                <Users size={20} />
              </div>
              <div className={styles.liveStatContent}>
                <span className={styles.liveStatLabel}>Utilisateurs en ligne</span>
                <strong className={styles.liveStatValue}>{liveStats.online_users ?? 0}</strong>
              </div>
            </div>

            <div className={styles.adminLiveStat}>
              <div className={styles.liveStatIcon}>
                <MessageSquare size={20} />
              </div>
              <div className={styles.liveStatContent}>
                <span className={styles.liveStatLabel}>Conversations actives</span>
                <strong className={styles.liveStatValue}>{liveStats.active_conversations ?? 0}</strong>
              </div>
            </div>

            <div className={styles.adminLiveStat}>
              <div className={styles.liveStatIcon}>
                <AlertTriangle size={20} />
              </div>
              <div className={styles.liveStatContent}>
                <span className={styles.liveStatLabel}>Erreurs (15 min)</span>
                <strong className={`${styles.liveStatValue} ${liveStats.errors_last_15min > 5 ? styles.error : ''}`}>
                  {liveStats.errors_last_15min ?? 0}
                </strong>
              </div>
            </div>

            <div className={styles.adminLiveStat}>
              <div className={styles.liveStatIcon}>
                <Brain size={20} />
              </div>
              <div className={styles.liveStatContent}>
                <span className={styles.liveStatLabel}>Requêtes IA/min</span>
                <strong className={styles.liveStatValue}>{liveStats.ai_requests_per_min ?? 0}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alertes récentes */}
      <div className={styles.adminAlertsSection}>
        <div className={styles.adminAlertsHeader}>
          <h2>
            <AlertTriangle size={20} />
            Alertes Récentes
          </h2>
          <Link to="/admin/system" className={styles.adminLink}>
            Voir toutes les alertes
            <ArrowUpRight size={14} />
          </Link>
        </div>

        {alerts && alerts.length > 0 ? (
          <div className={styles.adminAlertsList}>
            {alerts.slice(0, 5).map((alert) => (
              <div 
                key={alert.id} 
                className={`${styles.adminAlert} ${styles[`adminAlert${alert.severity?.charAt(0).toUpperCase()}${alert.severity?.slice(1)}`]}`}
              >
                <div className={styles.adminAlertIcon}>
                  {alert.severity === 'critical' && <AlertTriangle size={20} />}
                  {alert.severity === 'high' && <AlertTriangle size={20} />}
                  {alert.severity === 'medium' && <Activity size={20} />}
                  {alert.severity === 'low' && <CheckCircle size={20} />}
                </div>

                <div className={styles.adminAlertContent}>
                  <div className={styles.adminAlertTitle}>{alert.title}</div>
                  <div className={styles.adminAlertMeta}>
                    <span className={styles.adminAlertType}>{alert.type}</span>
                    <span className={styles.adminAlertTime}>
                      {new Date(alert.created_at).toLocaleString('fr-FR')}
                    </span>
                  </div>
                </div>

                <div className={styles.adminAlertBadge}>
                  {alert.severity}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.adminAlertsEmpty}>
            <CheckCircle size={48} />
            <p>Aucune alerte critique récente</p>
            <small>Tout fonctionne normalement 👍</small>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className={styles.adminDashboardFooter}>
        <div className={styles.adminFooterStats}>
          <div className={styles.adminFooterStat}>
            <Activity size={16} />
            <span>Système opérationnel</span>
            <strong className={styles.success}>99.9%</strong>
          </div>
          <div className={styles.adminFooterStat}>
            <Clock size={16} />
            <span>Dernière sauvegarde</span>
            <strong>{new Date().toLocaleTimeString('fr-FR')}</strong>
          </div>
          <div className={styles.adminFooterStat}>
            <Users size={16} />
            <span>Administrateurs</span>
            <strong>{admin_info.total_admins ?? 1}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;